import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { isSupported } from "@/lib/currency";
import { fetchClientIpGeo } from "@/lib/ipGeo";
import safeStorage from "@/lib/safeStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USER_GEO_KEY = "tc_user_geo";
const CURRENCY_KEY = "tc_currency_pref";
const CURRENCY_USER_FLAG_KEY = "tc_currency_pref_user_set"; // "1" if user picked manually
const COUNTRY_OVR_KEY = "tc_country_override";
const GEO_CONSENT_KEY = "tc_geo_consent"; // 'granted' | 'denied' | 'error'

const GeoContext = createContext({
    geo: null,
    loading: true,
    userGeo: null,
    setUserGeo: () => {},
    clearUserGeo: () => {},
    currencyPref: null,
    setCurrencyPref: () => {},
    currencyMap: {},
    countryToCurrency: {},
    geoConsent: null,
    requestPreciseLocation: async () => {},
});

function readJson(key) {
    try {
        const raw = safeStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

// Minimal local fallback used until /api/fx returns.
const FALLBACK_CURRENCY_MAP = {
    USD: { country_code: "US", affiliate_region: "WORLD" },
    INR: { country_code: "IN", affiliate_region: "IN" },
    EUR: { country_code: "FR", affiliate_region: "WORLD" },
    GBP: { country_code: "GB", affiliate_region: "WORLD" },
    AED: { country_code: "AE", affiliate_region: "WORLD" },
    SGD: { country_code: "SG", affiliate_region: "WORLD" },
    JPY: { country_code: "JP", affiliate_region: "WORLD" },
};
const FALLBACK_COUNTRY_TO_CCY = {
    US: "USD", IN: "INR", FR: "EUR", DE: "EUR", IT: "EUR", ES: "EUR", NL: "EUR",
    GB: "GBP", AE: "AED", SG: "SGD", MY: "MYR", TH: "THB", ID: "IDR",
    JP: "JPY", KR: "KRW", RU: "RUB", BR: "BRL", AR: "ARS", AU: "AUD",
};

async function reverseGeocode(lat, lon) {
    const r = await axios.post(`${API}/reverse-geocode`, { lat, lon });
    return r.data || null;
}

export function GeoProvider({ children }) {
    const [ipGeo, setIpGeo] = useState(null);
    const [loading, setLoading] = useState(true);

    const [userGeo, setUserGeoState] = useState(() => readJson(USER_GEO_KEY));
    const [currencyPref, setCurrencyPrefState] = useState(() => {
        const v = safeStorage.getItem(CURRENCY_KEY);
        return isSupported(v) ? v : null;
    });
    const [countryOverride, setCountryOverride] = useState(() => readJson(COUNTRY_OVR_KEY));
    const [currencyMap, setCurrencyMap] = useState(FALLBACK_CURRENCY_MAP);
    const [countryToCurrency, setCountryToCurrency] = useState(FALLBACK_COUNTRY_TO_CCY);
    const [geoConsent, setGeoConsent] = useState(() => safeStorage.getItem(GEO_CONSENT_KEY));

    // Always-current ref so geolocation callbacks (which capture closure at
    // mount time) can read the latest country→currency map.
    const c2cRef = useRef(countryToCurrency);
    useEffect(() => {
        c2cRef.current = countryToCurrency;
    }, [countryToCurrency]);

    // ── Auto-derive currency from country when the user hasn't manually picked one.
    const autoSetCurrencyFromCountry = useCallback((countryCode) => {
        if (!countryCode) return;
        // Honour manual override — do NOT touch user-picked currency.
        if (safeStorage.getItem(CURRENCY_USER_FLAG_KEY) === "1") return;
        const target = c2cRef.current[countryCode.toUpperCase()];
        if (!target || !isSupported(target)) return;
        if (safeStorage.getItem(CURRENCY_KEY) === target) return; // already there
        safeStorage.setItem(CURRENCY_KEY, target);
        setCurrencyPrefState(target);
        // Also clear any prior currency-driven country override so the new
        // location stays the source of truth for the deals rail.
        safeStorage.removeItem(COUNTRY_OVR_KEY);
        setCountryOverride(null);
    }, []);

    // ── 1. Auto-prompt geolocation on mount.
    //    Per Chrome's permission model, getCurrentPosition() must be called
    //    on a fresh, synchronous code path — no awaited fetch beforehand,
    //    no setTimeout, no Promise tick gap. We fire it immediately on the
    //    same tick the effect runs, BEFORE the IP-geo / FX work below.
    useEffect(() => {
        if (typeof navigator === "undefined" || !navigator.geolocation) return;
        if (userGeo) return; // already pinned from a prior visit
        const consent = safeStorage.getItem(GEO_CONSENT_KEY);
        if (consent === "denied") return;

        let cancelled = false;
        try {
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    if (cancelled) return;
                    try {
                        const data = await reverseGeocode(
                            pos.coords.latitude,
                            pos.coords.longitude,
                        );
                        if (!data) throw new Error("no data");
                        const next = {
                            city: data.city,
                            country_code: data.country_code,
                            country_name: data.country_name,
                            nearest_iata: data.nearest_iata,
                            region: data.region || null,
                            formatted_short: data.formatted_short || null,
                            lat: pos.coords.latitude,
                            lon: pos.coords.longitude,
                        };
                        safeStorage.setItem(USER_GEO_KEY, JSON.stringify(next));
                        setUserGeoState(next);
                        safeStorage.setItem(GEO_CONSENT_KEY, "granted");
                        setGeoConsent("granted");
                        // Country → currency, unless user manually picked.
                        autoSetCurrencyFromCountry(data.country_code);
                    } catch {
                        safeStorage.setItem(GEO_CONSENT_KEY, "error");
                        setGeoConsent("error");
                    }
                },
                (err) => {
                    if (cancelled) return;
                    const code = err && err.code;
                    // 1 = denied (treat as denied), 2 = unavailable, 3 = timeout (don't latch)
                    const next = code === 1 ? "denied" : "error";
                    safeStorage.setItem(GEO_CONSENT_KEY, next);
                    setGeoConsent(next);
                },
                // maximumAge: 0 forces a fresh prompt — no silent reuse of
                // a stale fix from another tab.
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
            );
        } catch {
            // Permissions-Policy or insecure context — don't latch denied.
            safeStorage.setItem(GEO_CONSENT_KEY, "error");
            setGeoConsent("error");
        }
        return () => {
            cancelled = true;
        };
        // Mount-only. We don't list deps because we want the prompt to fire
        // on the very first synchronous mount tick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── 2. IP geo + FX map (independent, doesn't block GPS prompt).
    //    Primary: /api/geo/ip-lookup (server-side ipinfo.io + 24h Mongo
    //    cache). The browser-side providers (ipwho.is / ipapi.co /
    //    country.is) often fail behind corporate proxies or strict CSPs,
    //    leaving the chip stuck on the country fallback. The backend
    //    endpoint is hit through the same origin we're already trusting
    //    for /api/chat, so it never gets CORS-blocked.
    useEffect(() => {
        let cancelled = false;
        const ipPromise = (async () => {
            // 1. Backend ip-lookup first (most reliable, includes city).
            try {
                const r = await axios.get(`${API}/geo/ip-lookup`);
                const d = r.data || {};
                if (d.city || d.country_code) {
                    return {
                        country_code: (d.country_code || "").toUpperCase(),
                        country_name: d.country_name || d.country_code,
                        city: d.city || null,
                        region: d.region || null,
                        currency: null,
                        latitude: null,
                        longitude: null,
                        affiliate_region:
                            d.country_code === "IN" ? "IN" : "WORLD",
                        source: "backend:ip-lookup",
                    };
                }
            } catch { /* fall through */ }
            // 2. Browser-side public providers.
            const client = await fetchClientIpGeo();
            if (client) {
                return {
                    country_code: client.country_code,
                    country_name: client.country_name || client.country_code,
                    city: client.city,
                    region: client.region,
                    currency: client.currency || null,
                    latitude: client.latitude,
                    longitude: client.longitude,
                    affiliate_region:
                        client.country_code === "IN" ? "IN" : "WORLD",
                    source: client.source,
                };
            }
            // 3. Last-resort: legacy /api/geo.
            try {
                const r = await axios.get(`${API}/geo`);
                return { ...r.data, source: "backend:geo" };
            } catch {
                return null;
            }
        })();

        Promise.allSettled([ipPromise, axios.get(`${API}/fx`)]).then(([g, fx]) => {
            if (cancelled) return;
            const data = g.status === "fulfilled" ? g.value : null;
            if (data) {
                setIpGeo(data);
                // Only honour the IP country for currency if GPS hasn't
                // already produced a `userGeo`. GPS is more authoritative
                // and may have resolved AFTER this fetch was kicked off
                // but BEFORE this callback runs (race).
                if (!safeStorage.getItem(USER_GEO_KEY)) {
                    autoSetCurrencyFromCountry(data.country_code);
                }
                // Fallback: if we still don't have a currency at all,
                // honour the IP geo's currency hint.
                setCurrencyPrefState((cur) => {
                    if (cur) return cur;
                    const c = data.currency;
                    return isSupported(c) ? c : "USD";
                });
            } else {
                setIpGeo({
                    country_code: "US",
                    country_name: "United States",
                    currency: "USD",
                    affiliate_region: "WORLD",
                });
                setCurrencyPrefState((cur) => cur || "USD");
            }
            if (fx.status === "fulfilled") {
                const fxd = fx.value.data || {};
                if (fxd.currency_to_country) setCurrencyMap(fxd.currency_to_country);
                if (fxd.country_to_currency) setCountryToCurrency(fxd.country_to_currency);
            }
            setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [autoSetCurrencyFromCountry]);

    // ── Public setters
    const setUserGeo = useCallback(
        (g) => {
            if (g) safeStorage.setItem(USER_GEO_KEY, JSON.stringify(g));
            else safeStorage.removeItem(USER_GEO_KEY);
            setUserGeoState(g || null);
            if (g) {
                safeStorage.setItem(GEO_CONSENT_KEY, "granted");
                setGeoConsent("granted");
                // Manual 📍 click resolves to a new country → re-derive currency.
                autoSetCurrencyFromCountry(g.country_code);
            }
        },
        [autoSetCurrencyFromCountry],
    );

    const clearUserGeo = useCallback(() => {
        safeStorage.removeItem(USER_GEO_KEY);
        setUserGeoState(null);
    }, []);

    const setCurrencyPref = useCallback(
        (code, opts) => {
            if (!isSupported(code)) return;
            safeStorage.setItem(CURRENCY_KEY, code);
            setCurrencyPrefState(code);
            // Manual selection from the dropdown → latch the user-set flag.
            if (opts && opts.fromUser) {
                safeStorage.setItem(CURRENCY_USER_FLAG_KEY, "1");
            }
            const ovr = currencyMap[code];
            if (ovr) {
                safeStorage.setItem(COUNTRY_OVR_KEY, JSON.stringify(ovr));
                setCountryOverride(ovr);
            }
        },
        [currencyMap],
    );

    /**
     * User-initiated retry triggered by the "Use precise location" link.
     * Bypasses the `denied` latch because the browser may honour a fresh
     * user gesture. Always derives currency from the resolved country
     * (still respecting the user-manual flag).
     */
    const requestPreciseLocation = useCallback(async () => {
        if (typeof navigator === "undefined" || !navigator.geolocation) return;
        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    try {
                        const data = await reverseGeocode(
                            pos.coords.latitude,
                            pos.coords.longitude,
                        );
                        if (!data) {
                            resolve();
                            return;
                        }
                        const next = {
                            city: data.city,
                            country_code: data.country_code,
                            country_name: data.country_name,
                            nearest_iata: data.nearest_iata,
                            region: data.region || null,
                            formatted_short: data.formatted_short || null,
                            lat: pos.coords.latitude,
                            lon: pos.coords.longitude,
                        };
                        safeStorage.setItem(USER_GEO_KEY, JSON.stringify(next));
                        setUserGeoState(next);
                        safeStorage.setItem(GEO_CONSENT_KEY, "granted");
                        setGeoConsent("granted");
                        autoSetCurrencyFromCountry(data.country_code);
                    } finally {
                        resolve();
                    }
                },
                () => {
                    safeStorage.setItem(GEO_CONSENT_KEY, "denied");
                    setGeoConsent("denied");
                    resolve();
                },
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
            );
        });
    }, [autoSetCurrencyFromCountry]);

    // The exposed `geo` prioritises, in order:
    //   1. `countryOverride` — set when the user manually switches currency
    //      and wants the deal rail / affiliate region to follow that market.
    //   2. `userGeo` — GPS-resolved country. This is the fix for the stale
    //      country bug: when GPS resolves to a new country, the deal rail
    //      and `/api/chat`'s `affiliate_region` must follow immediately.
    //   3. `ipGeo` — fallback when no more-specific signal exists.
    const geo = (() => {
        if (!ipGeo) return null;
        if (countryOverride) {
            return {
                ...ipGeo,
                country_code: countryOverride.country_code,
                affiliate_region: countryOverride.affiliate_region,
            };
        }
        if (userGeo && userGeo.country_code) {
            const cc = userGeo.country_code.toUpperCase();
            const ccy = countryToCurrency[cc];
            const aff = (ccy && currencyMap[ccy]?.affiliate_region) || "WORLD";
            return {
                ...ipGeo,
                country_code: cc,
                country_name: userGeo.country_name || ipGeo.country_name,
                affiliate_region: aff,
                city: userGeo.city || ipGeo.city,
            };
        }
        return ipGeo;
    })();

    return (
        <GeoContext.Provider
            value={{
                geo,
                loading,
                userGeo,
                setUserGeo,
                clearUserGeo,
                currencyPref: currencyPref || ipGeo?.currency || "USD",
                setCurrencyPref,
                currencyMap,
                countryToCurrency,
                geoConsent,
                requestPreciseLocation,
            }}
        >
            {children}
        </GeoContext.Provider>
    );
}

export function useGeo() {
    return useContext(GeoContext);
}
